import { useCallback, useEffect, useState } from "react";
import { Cpu, Check, Sparkles } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const TIER_ORDER = ["Flagship", "Standard", "Reasoning", "Fast / low-cost", "Legacy"];

export default function AIModels() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const load = useCallback(() => { api.get("/ai-models/config").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { available, selected, recommended, capabilities } = data;

  const choose = async (id) => {
    if (id === selected) return;
    setSaving(id);
    try { await api.post("/ai-models/config", { model: id }); toast.success(`Capital Crew now runs on ${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); } finally { setSaving(null); }
  };

  const groups = TIER_ORDER.map((t) => [t, available.filter((m) => m.tier === t)]).filter(([, arr]) => arr.length);

  return (
    <div className="animate-fade-up space-y-6" data-testid="ai-models-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Cpu size={22} className="text-[#b0862f]" /> ChatGPT Models</h1>
        <p className="mt-1 text-[13px] text-slate-500">Choose which OpenAI ChatGPT model your Capital Crew uses. Powered by your Emergent universal key.</p>
      </div>

      <Card className="p-4" data-testid="current-model">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Active model</span><div className="font-head text-xl font-extrabold text-slate-900">{selected}</div></div>
          <div className="text-[12px] text-slate-500">Used for: {capabilities.join(" · ")}</div>
        </div>
      </Card>

      {groups.map(([tier, models]) => (
        <div key={tier}>
          <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">{tier}</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {models.map((m) => {
              const active = m.id === selected;
              return (
                <button key={m.id} onClick={() => choose(m.id)} disabled={saving === m.id} data-testid={`model-${m.id}`}
                  className={`flex items-center justify-between rounded-xl border p-4 text-left transition-colors ${active ? "border-[#080d1a] bg-[#080d1a] text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div>
                    <div className={`font-mono text-[14px] font-bold ${active ? "text-white" : "text-slate-900"}`}>{m.id}</div>
                    {m.id === recommended && <span className={`mt-1 inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-wider ${active ? "text-[#b0862f]" : "text-emerald-600"}`}><Sparkles size={10} /> Recommended</span>}
                  </div>
                  {active && <Check size={18} className="text-[#b0862f]" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
